import formatDate from 'date-fns/format';
import React, { useMemo, useState } from 'react';
import { Box, IconButton, Theme, Tooltip, Typography } from '@mui/material';
import {
  CurrencyBalanceAttributes,
  ProfileAttributes,
} from '@loyalty/shared-common/profile-api-client';
import { Feature, getLocalizedProperty } from '@loyalty/shared-common';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { colorPalette } from '@qualifio/ui-commons';
import { faPencil } from '@fortawesome/pro-solid-svg-icons/faPencil';
import { faUsdCircle } from '@fortawesome/pro-solid-svg-icons/faUsdCircle';
import { makeStyles } from '@mui/styles';
import { useTranslation } from 'react-i18next';
import { AdjustBalanceDialog } from './AdjustBalanceDialog';
import { CurrencyImage } from '../../../shared/currency/CurrencyImage';
import { formatNumber } from '../../../shared/utils';
import { useCurrencies } from '../../../currency/CurrenciesContext';
import { useFeatures } from '../../../features/FeaturesContext';
import { useProgram } from '../../../loyalty/ProgramContext';

const useStyles = makeStyles<Theme>(({ spacing }) => ({
  list: {
    margin: 0,
    padding: 0,
  },
  listItem: {
    display: 'flex',
    alignItems: 'center',
    padding: spacing(1, 0),
  },
  item: {
    padding: spacing(0, 1),
  },
  property: {
    fontWeight: 600,
  },
  image: {
    height: 26,
    width: 26,
    '& img': {
      height: '100%',
      width: '100%',
    },
  },
  amount: {
    marginRight: spacing(0.5),
  },
  totalAmount: {
    marginRight: spacing(0.5),
  },
  iconButton: {
    color: colorPalette.grey.main,
    // minWidth: 40,
    '&:hover': {
      backgroundColor: 'inherit',
      color: colorPalette.turquoise.main,
    },
  },
}));

interface Props {
  profile: ProfileAttributes;
  onBalanceAdjusted?: () => Promise<void> | void;
}

export function ProfileCurrencyList({ profile, onBalanceAdjusted }: Props): React.ReactElement {
  const { currencies } = useCurrencies();
  const { program } = useProgram();
  const classes = useStyles();
  const { i18n } = useTranslation();
  const [adjustedBalance, setAdjustedBalance] = useState<string | undefined>();
  const { features } = useFeatures();
  const { enabled: adjustBalanceEnabled } = features[Feature.ADJUST_BALANCE];
  const { t } = useTranslation();
  const timeDateFormat = t('dateFormat') ?? 'dd/MM/yyyy';

  const getCurrency = (currencyId: string) => currencies.find(({ id }) => id === currencyId);

  const programBalances =
    profile.currencies?.filter(({ programId }) => programId === program.id) || [];

  const balances = useMemo(
    () =>
      currencies.map(
        currency =>
          programBalances.find(balance => balance.currencyId === currency.id) ||
          ({
            id: `${currency.id}-balance`,
            currencyId: currency.id,
            value: 0,
          } as CurrencyBalanceAttributes),
      ),
    [profile, program.id, programBalances, currencies],
  );

  return (
    <ul className={classes.list}>
      {balances.map(balance => {
        const currency = getCurrency(balance.currencyId);

        return currency ? (
          <Box key={balance.id} component="li" className={classes.listItem}>
            <Tooltip
              title={
                balance.nextExpiration
                  ? `${formatNumber(balance.nextExpiration.amount)} ${t(
                      'profile.currency.expireOn',
                    )} ${formatDate(new Date(balance.nextExpiration.date), timeDateFormat)}`
                  : ''
              }
            >
              <div>
                <CurrencyImage currency={currency} />
              </div>
            </Tooltip>
            <Box className={classes.item}>
              <Typography variant="body1" component="div" className={classes.property}>
                {getLocalizedProperty(currency, 'name', i18n.language)}
              </Typography>
            </Box>
            <Box className={classes.item}>
              <Typography variant="body1" component="div" className={classes.property}>
                <span className={classes.amount}>
                  {formatNumber(balance.value || 0, i18n.language)}
                </span>
                <FontAwesomeIcon icon={faUsdCircle} size="sm" />
                {/*<span className={classes.totalAmount}>*/}
                {/*  (Total: {formatNumber(balance.value || 0, i18n.language)})*/}
                {/*</span>*/}
              </Typography>
            </Box>
            <Box>
              <Tooltip title={adjustBalanceEnabled ? '' : t('shared.action.permission.required')}>
                <div>
                  <IconButton
                    disabled={!adjustBalanceEnabled}
                    className={classes.iconButton}
                    onClick={() => setAdjustedBalance(balance.id)}
                    id={'adjust-balance-button-id'}
                  >
                    <FontAwesomeIcon size="xs" icon={faPencil} />
                  </IconButton>
                </div>
              </Tooltip>
              {adjustedBalance === balance.id && (
                <AdjustBalanceDialog
                  profile={profile}
                  balance={balance}
                  currency={currencies.find(({ id }) => id === balance.currencyId)}
                  onClose={() => setAdjustedBalance(undefined)}
                  onApply={onBalanceAdjusted}
                />
              )}
            </Box>
          </Box>
        ) : null;
      })}
    </ul>
  );
}
